import React from 'react'
import Text from '@amzn/meridian/text'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { IStoreItemProps } from '../types'
import { BADGE_CURRENT, BADGE_SELECTED, getStoreAriaLabelId } from '../constants'
import './StoreItem.scss'

/**
 * StoreItem Component
 *
 * Displays a single store in the store list with click and hover interactions.
 * Shows "Current" badge for the current store and "Selected" badge for the selected store.
 * Gray background appears on selected store, or current store if no selection.
 */
export const StoreItem: React.FC<IStoreItemProps> = ({
    store,
    onClick,
    isCurrent = false,
    isSelected = false,
}) => {
    const [bundle] = useBundle(BUNDLE_NAME)

    const handleClick = () => {
        onClick(store.storeId)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleClick()
        }
    }

    const className = `store-item${isSelected ? ' selected' : ''}`

    return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={className}
      data-atb-id="store-item"
      aria-label={t(bundle, getStoreAriaLabelId(isCurrent, isSelected), {
          storeName: store.storeName || store.storeId,
      })}
    >
      <Text data-atb-id="store-item-name">{store.storeName || store.storeId}</Text>
      <div className="badge-container">
        {isCurrent && (
          <Text className="store-badge" data-atb-id="store-badge-current" type="b300">{t(bundle, BADGE_CURRENT)}</Text>
        )}
        {isSelected && !isCurrent && (
          <Text className="store-badge" data-atb-id="store-badge-selected" type="b300">{t(bundle, BADGE_SELECTED)}</Text>
        )}
      </div>
    </div>
    )
}
